import {
	type DiffNodeRef,
	type GraphDiff,
	NODE_TYPE_LABELS,
	type NodeType,
} from "@velizhanin-dev/bot-scenario"
import type { ReactNode } from "react"
import type { ScenarioDraft } from "@/entities/bot-builder"
import { cn } from "@/shared/lib/utils"

/** Сколько всего поменялось: для заголовка и истории версий. */
export function countDiff(diff: GraphDiff): number {
	return (
		diff.nodes.added.length +
		diff.nodes.changed.length +
		diff.nodes.removed.length +
		diff.flows.added.length +
		diff.flows.renamed.length +
		(diff.flows.changed?.length ?? 0) +
		diff.flows.removed.length +
		diff.variables.added.length +
		diff.variables.changed.length +
		diff.variables.removed.length +
		(diff.tags?.added.length ?? 0) +
		(diff.tags?.renamed.length ?? 0) +
		(diff.tags?.removed.length ?? 0) +
		(diff.entryLinks?.added.length ?? 0) +
		(diff.entryLinks?.changed.length ?? 0) +
		(diff.entryLinks?.removed.length ?? 0) +
		(diff.menuChanged ? 1 : 0)
	)
}

function DiffGroup({ title, tone, items }: { title: string; tone: string; items: ReactNode[] }) {
	if (items.length === 0) return null
	return (
		<div>
			<p className={cn("mb-1 text-xs font-medium", tone)}>
				{title} · {items.length}
			</p>
			<ul className="space-y-0.5 text-sm">
				{items.map((item, index) => (
					<li key={index} className="truncate">
						{item}
					</li>
				))}
			</ul>
		</div>
	)
}

const nodeLine = (node: DiffNodeRef, flows: Map<string, string>) => (
	<>
		<span className="text-muted-foreground">
			{NODE_TYPE_LABELS[node.type as NodeType] ?? node.type}
			{flows.get(node.flowId) ? ` · ${flows.get(node.flowId)}` : ""}:
		</span>{" "}
		{node.summary}
	</>
)

/** Отличия версий по-человечески. Потоки удалённых блоков могут уже не существовать. */
export function DiffView({ diff, draft }: { diff: GraphDiff; draft: ScenarioDraft }) {
	const flows = new Map(draft.flows.map((flow) => [flow.id, flow.name]))
	return (
		<div className="space-y-3">
			<DiffGroup
				title="Новые блоки"
				tone="text-emerald-400"
				items={diff.nodes.added.map((node) => nodeLine(node, flows))}
			/>
			<DiffGroup
				title="Изменённые блоки"
				tone="text-yellow-400"
				items={diff.nodes.changed.map((node) => nodeLine(node, flows))}
			/>
			<DiffGroup
				title="Удалённые блоки"
				tone="text-red-400"
				items={diff.nodes.removed.map((node) => nodeLine(node, flows))}
			/>
			<DiffGroup
				title="Воронки"
				tone="text-muted-foreground"
				items={[
					...diff.flows.added.map((name) => `＋ ${name}`),
					...diff.flows.renamed.map(({ from, to }) => `${from} → ${to}`),
					...(diff.flows.changed ?? []).map((name) => `⚡ ${name}: команды и слова запуска`),
					...diff.flows.removed.map((name) => `− ${name}`),
				]}
			/>
			<DiffGroup
				title="Переменные"
				tone="text-muted-foreground"
				items={[
					...diff.variables.added.map((name) => `＋ ${name}`),
					...diff.variables.changed.map((name) => `✎ ${name}`),
					...diff.variables.removed.map((name) => `− ${name}`),
				]}
			/>
			<DiffGroup
				title="Группы"
				tone="text-muted-foreground"
				items={[
					...(diff.tags?.added ?? []).map((name) => `＋ ${name}`),
					...(diff.tags?.renamed ?? []).map(({ from, to }) => `${from} → ${to}`),
					...(diff.tags?.removed ?? []).map((name) => `− ${name}`),
				]}
			/>
			<DiffGroup
				title="UTM-метки"
				tone="text-muted-foreground"
				items={[
					...(diff.entryLinks?.added ?? []).map((code) => `＋ ${code}`),
					...(diff.entryLinks?.changed ?? []).map((code) => `✎ ${code}`),
					...(diff.entryLinks?.removed ?? []).map((code) => `− ${code}`),
				]}
			/>
			<DiffGroup
				title="Меню бота"
				tone="text-muted-foreground"
				items={diff.menuChanged ? ["✎ главное меню или кнопка у поля ввода"] : []}
			/>
		</div>
	)
}
